<template>
    <div ref="root">
        <purchase-contract-section>
            <template #title>
                <div class="flex w-full items-center justify-between">
                    <div class="flex flex-col gap-1 md:flex-row">
                        <div>
                            {{ t('purchaseContract.selectParticipants') }}
                        </div>
                        <div class="font-normal">
                            <span class="hidden lg:inline">- </span
                            >{{ activeFacility.shortName }}
                        </div>
                    </div>

                    <prime-button
                        :class="[
                            contractData !== undefined && !isActiveSection
                                ? ''
                                : 'invisible'
                        ]"
                        @click="emit('editSection')"
                    >
                        <div class="flex items-center gap-2">
                            <i class="pi pi-pencil" />
                            <span class="lg:hidden">{{ t('edit') }}</span>
                            <span class="hidden lg:inline">{{
                                t('purchaseContract.changeParticipants')
                            }}</span>
                        </div>
                    </prime-button>
                </div>
            </template>

            <div
                v-if="contractData !== undefined && !isActiveSection"
                class="flex flex-wrap gap-x-8 gap-y-4"
            >
                <div>
                    <label class="block font-semibold">{{
                        t('purchaseContract.participants')
                    }}</label>
                    <ul class="list-disc pl-6">
                        <li
                            v-for="enrollmentSpecification in contractData.enrollmentSpecifications"
                            :key="enrollmentSpecification.enrollmentType.id"
                        >
                            {{ enrollmentSpecification.count }}
                            {{
                                enrollmentSpecification.enrollmentType.singular
                            }}
                        </li>
                    </ul>
                </div>
            </div>

            <div
                v-if="isActiveSection"
                class="flex flex-col gap-8 xl:flex-row"
            >
                <div class="flex flex-wrap gap-8">
                    <enrollment-type-selectors
                        v-model:enrollment-input="enrollmentInput"
                    />
                </div>

                <div class="flex-1 space-y-4">
                    <prime-message
                        v-if="
                            continueAttempts > 0 &&
                            bookingLimitsErrorMessage !== null
                        "
                        severity="error"
                        :closable="false"
                    >
                        {{ bookingLimitsErrorMessage }}
                    </prime-message>
                </div>
            </div>

            <div
                v-if="isActiveSection"
                class="mt-6"
            >
                <prime-button
                    v-if="contractData === undefined"
                    :label="t('continue')"
                    :loading="isMutatingContract || isLoadingVideo"
                    class="gap-2"
                    :disabled="bookingLimitsErrorMessage !== null"
                    @click="handleContinueButton"
                />

                <prime-button
                    v-else
                    :label="t('continue')"
                    :loading="isMutatingContract"
                    class="gap-2"
                    @click="handleSaveChanges"
                />
            </div>

            <span
                v-if="isActiveSection && videoId !== null"
                :class="`wistia_embed wistia_async_${videoId} popover=true popoverContent=link relative inline`"
            />

            <teleport
                v-if="isWatchingVideo"
                to="body"
            >
                <div
                    class="text-button fixed bottom-4 left-1/2 z-[10001] -translate-x-1/2 transform bg-primary-600"
                >
                    <div
                        class="hidden whitespace-nowrap px-10 py-4 text-xl lg:block [@media(max-height:500px)]:hidden"
                    >
                        {{ t('purchaseContract.safetyVideoHelp') }}
                    </div>
                </div>
            </teleport>

            <teleport
                v-if="showAlreadyWatchButton"
                to="body"
            >
                <div class="fixed bottom-4 right-4 z-[10001]">
                    <prime-button
                        :label="t('purchaseContract.alreadyWatched')"
                        @click="handleAlreadyWatched"
                    />
                </div>
            </teleport>
        </purchase-contract-section>
    </div>
</template>

<script lang="ts">
import { graphql } from '~/resources/graphql'

export const SelectAgreementParticipantsPlanFragment = graphql(/** GraphQL */ `
    fragment SelectAgreementParticipantsPlanFragment on Plan {
        sessions(facilityId: $facilityId) {
            id
            sessionFacilityHash
        }
        terms {
            allowGuestCheckout
            enrollmentRanges {
                enrollmentType {
                    id
                    singular
                    plural
                    requireContractHolder
                    ageEnforcementMode
                    ageMinimum
                }
                minQuantity
                maxQuantity
            }
        }
        bookingLimit(input: $input) {
            minimumEnrollments
            maximumEnrollments
        }
        presentation(input: $input, language: $language) {
            wistiaVideoId
            videoUrl
            allowSkippingWistiaVideo
        }
        contractDateOptions(input: $input) {
            startDate
            endDate
        }
    }
`)

export const SelectAgreementParticipantsContractFragment = graphql(
    /** GraphQL */ `
        fragment SelectAgreementParticipantsContractFragment on PlanContract {
            id
            updatedAtUtc
            startDateLocal
            endDateLocal
            facility {
                shortName
            }
            enrollmentSpecifications {
                enrollmentType {
                    id
                    singular
                    plural
                }
                count
                enrollments {
                    customer {
                        id
                        commaName
                    }
                }
            }
        }
    `
)
</script>

<script setup lang="ts">
import { useShowLoginModal, useViewer } from '~/composables/use-login'
import PrimeButton from 'primevue/button'
import { ref, onMounted, computed, watch } from 'vue'
import { useActiveFacility, useLayoutMode, useStorefront } from '#imports'
import type { FragmentType } from '~/resources/graphql'
import { getFragmentData } from '~/resources/graphql'
import type {
    ChangeContractInput,
    CreateContractInput
} from '~/resources/graphql/graphql'
import EnrollmentTypeSelectors from '~/components/PurchaseContract/EnrollmentTypeSelectors.vue'
import PrimeMessage from 'primevue/message'
import PurchaseContractSection from '~/components/PurchaseContract/PurchaseContractSection.vue'
import { useAgreementVideo } from '~/components/PurchaseContract/Agreement/use-agreement-video'
import { useI18n } from 'vue-i18n'
import { useAgreementWistia } from '~/components/PurchaseContract/Agreement/use-agreement-wistia'

const props = defineProps<{
    plan: FragmentType<typeof SelectAgreementParticipantsPlanFragment>
    contract:
        | FragmentType<typeof SelectAgreementParticipantsContractFragment>
        | undefined
    isActiveSection: boolean
    isMutatingContract: boolean
}>()

const emit = defineEmits<{
    (e: 'createContract', payload: CreateContractInput): void
    (e: 'changeContract', payload: ChangeContractInput): void
    (e: 'editSection'): void
}>()

const root = ref<HTMLDivElement>()
onMounted(() => {
    // Only scroll to bottom if we have already started a contract
    if (props.contract !== undefined) {
        root.value?.scrollIntoView({
            behavior: 'smooth'
        })
    }
})

const planData = computed(() =>
    getFragmentData(SelectAgreementParticipantsPlanFragment, props.plan)
)

const contractData = computed(() =>
    props.contract === undefined
        ? props.contract
        : getFragmentData(
              SelectAgreementParticipantsContractFragment,
              props.contract
          )
)
const activeFacility = useActiveFacility()
const { t } = useI18n({ useScope: 'global' })

const enrollmentInput = ref(
    planData.value.terms.enrollmentRanges.map(range => {
        return {
            enrollmentType: {
                id: range.enrollmentType.id,
                singular: range.enrollmentType.singular,
                plural: range.enrollmentType.plural
            },
            minQuantity: range.minQuantity,
            maxQuantity: range.maxQuantity,
            count: range.minQuantity
        }
    })
)

// Compute the enrollment input for the mutation
const enrollmentTypeCounts = computed(() => {
    return enrollmentInput.value
        .filter(element => element.count > 0)
        .map(element => {
            return {
                enrollmentTypeId: element.enrollmentType.id,
                count: element.count
            }
        })
})

const totalEnrollmentCount = computed(() => {
    let totalEnrollmentCount = 0
    enrollmentInput.value.forEach(enrollmentType => {
        totalEnrollmentCount += enrollmentType.count
    })
    return totalEnrollmentCount
})

const continueAttempts = ref(0)
const bookingLimitsErrorMessage = computed(() => {
    if (
        planData.value.bookingLimit.minimumEnrollments !== null &&
        totalEnrollmentCount.value <
            planData.value.bookingLimit.minimumEnrollments
    ) {
        return t('purchaseContract.agreementMinimumBookingLimit', {
            minimumEnrollments: planData.value.bookingLimit.minimumEnrollments
        })
    }
    if (
        planData.value.bookingLimit.maximumEnrollments !== null &&
        totalEnrollmentCount.value >
            planData.value.bookingLimit.maximumEnrollments
    ) {
        return t('purchaseContract.maximumBookingLimit', {
            maximumEnrollments: planData.value.bookingLimit.maximumEnrollments
        })
    }

    return null
})

const session = ref(planData.value.sessions[0])
// Booking in ahead is not available on agreements
// So, we can be sure there will be only one date option and select that
const contractStartDate = ref<string | null>(
    planData.value.contractDateOptions[0].startDate
)
const contractEndDate = ref<string | null>(
    planData.value.contractDateOptions[0].endDate
)

const ageOfMajorityHint = computed(() => {
    const contractHolderType = planData.value.terms.enrollmentRanges.find(
        range =>
            range.enrollmentType.requireContractHolder &&
            range.enrollmentType.ageEnforcementMode === 'ENFORCE_IN_CPX'
    )

    if (contractHolderType !== undefined) {
        return contractHolderType.enrollmentType.ageMinimum
    }

    return 0
})

const viewer = useViewer()
const showLoginModal = useShowLoginModal()
const videoId = planData.value.presentation?.wistiaVideoId ?? null
const videoUrl = planData.value.presentation?.videoUrl ?? null
const allowSkippingVideo =
    planData.value.presentation?.allowSkippingWistiaVideo ?? false

const { isKiosk } = useLayoutMode()
let useVideoUrl = false
if (videoUrl !== null) {
    // Only video url enabled, no wistia, then use the video url
    if (videoId === null) {
        useVideoUrl = true
    }
    // Both enabled, then use the video url on the kiosk, otherwise use wistia
    else {
        if (isKiosk.value) {
            useVideoUrl = true
        } else {
            useVideoUrl = false
        }
    }
}

const {
    isLoadingVideo,
    isWatchingVideo,
    showAlreadyWatchButton,
    handleWatchVideo,
    handleAlreadyWatched
} = useVideoUrl
    ? useAgreementVideo(
          videoUrl,
          allowSkippingVideo,
          validateContinue,
          handleContinue
      )
    : useAgreementWistia(
          videoId,
          allowSkippingVideo,
          validateContinue,
          handleContinue
      )

function validateContinue() {
    continueAttempts.value += 1
    if (bookingLimitsErrorMessage.value !== null) {
        return false
    }

    return true
}

const storefront = useStorefront()
const allowGuestCheckout = computed<boolean>(
    () =>
        storefront.value.allowGuestCheckout &&
        planData.value.terms.allowGuestCheckout
)
let videoCustomerId = ''
async function handleContinueButton() {
    if (!validateContinue()) {
        return
    }

    // Get the customer id
    let customerId = viewer.value?.customerId
    if (customerId === undefined) {
        customerId = await showLoginModal({
            showGuest: allowGuestCheckout.value,
            ageOfMajorityHint: ageOfMajorityHint.value
        })
        if (customerId === undefined) {
            return
        }
    }

    if (videoUrl !== null || videoId !== null) {
        videoCustomerId = customerId
        await handleWatchVideo()
    } else {
        handleCreateContract(customerId)
    }
}

async function handleContinue() {
    if (!validateContinue()) {
        return
    }

    handleCreateContract(videoCustomerId)
}

const { isGroup, groupBookingId } = useLayoutMode()

function handleCreateContract(customerId: string) {
    isLoadingVideo.value = false

    emit('createContract', {
        customerId,
        sessionFacilityHash: session.value.sessionFacilityHash,
        startDate: contractStartDate.value,
        endDate: contractEndDate.value,
        // for the purpose of reserving slots
        enrollmentTypeCounts: enrollmentTypeCounts.value,
        total: '0',
        subtotal: '0',
        parentGroupBookingId: isGroup.value ? groupBookingId.value : null
    })
}

function handleSaveChanges() {
    if (contractData.value === undefined) {
        // No contract to update so error
        throw new Error()
    }

    continueAttempts.value += 1

    if (bookingLimitsErrorMessage.value !== null) {
        return
    }

    emit('changeContract', {
        id: contractData.value.id,
        updatedAt: contractData.value.updatedAtUtc,
        // Rename startSessionFacilityHash
        startSessionFacilityHash: session.value.sessionFacilityHash,
        startDate: contractStartDate.value,
        endDate: contractEndDate.value,
        // for the purpose of reserving slots
        enrollmentTypeCounts: enrollmentTypeCounts.value,
        total: '0',
        subtotal: '0'
    })
}

watch(
    contractData,
    newValue => {
        if (newValue === undefined) {
            return
        }

        enrollmentInput.value = enrollmentInput.value.map(input => {
            const contractInput = newValue.enrollmentSpecifications.find(
                spec => spec.enrollmentType.id === input.enrollmentType.id
            )

            if (contractInput === undefined) {
                return { ...input }
            }

            return {
                ...input,
                count: contractInput.count
            }
        })
    },
    { immediate: true }
)
</script>
